/**
 * ReservationsServicesTab - Service reservations table (matches legacy format)
 * Features: Table view, payment tracking, filtering, same as legacy services tab
 */

import React, { useState, useEffect, useCallback, memo, useRef } from 'react';
import {
  UserIcon,
  EyeIcon,
  ArrowPathIcon,
  XCircleIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import axios from 'axios';
import LoadingSpinner from '../../../common/LoadingSpinner';
import EmptyState from '../../../common/EmptyState';
import Pagination from '../../../common/Pagination';
import ReservationDetailsModal from '../../reservations.shared/components/ReservationDetailsModal';
import CheckInModeTable from './CheckInModeTable';
import { useAuth } from '../../../../contexts/AuthContext';
import { useReservations } from '../../reservations.shared/context/ReservationsContext';
import { API_BASE_URL } from '../../../../config/api';

const ReservationsServicesTab = ({ globalFilters = {}, services = [], customers = [] }) => {
  const { canProcessReturns, isAdmin, hasPermission } = useAuth();
  const { orgTaxRate } = useReservations();
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState(globalFilters.status || 'all');
  
  // Use global filters from parent component
  const searchTerm = globalFilters.searchTerm || '';
  const serviceFilter = globalFilters.serviceFilter || '';
  const customerFilter = globalFilters.customerFilter || '';
  const [currentPage, setCurrentPage] = useState(1);

  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: ''
  });
  
  // Payment tracking state for services (matches legacy)
  const [paymentStatus, setPaymentStatus] = useState({}); // { reservationId: { isPaid: boolean, amount: string, source: string } }
  
  // Dynamic payment sources based on available services
  const [availablePaymentMethods, setAvailablePaymentMethods] = useState(new Set());
  
  // Debounce timer for amount field updates
  const debounceTimers = useRef({});
  
  // Modal states
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailedReservation, setDetailedReservation] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Sorting state
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc'

  // Check-in mode state
  const [isCheckInMode, setIsCheckInMode] = useState(false);

  const itemsPerPage = 50;

  // Format date and time for display - properly convert UTC to local time
  const formatDateTime = useCallback((dateTime) => {
    if (!dateTime) return { date: 'N/A', time: 'N/A' };
    
    try {
      // Ensure we're parsing as UTC and converting to local time
      const date = new Date(dateTime + (dateTime.includes('Z') ? '' : 'Z'));
      const dateOptions = { month: 'short', day: 'numeric' };
      const timeOptions = { hour: 'numeric', minute: '2-digit', hour12: true };
      
      return {
        date: date.toLocaleDateString('en-US', dateOptions),
        time: date.toLocaleTimeString('en-US', timeOptions)
      };
    } catch (error) {
      console.error('Error formatting date:', error, 'for dateTime:', dateTime);
      return { date: 'Invalid', time: 'Date' };
    }
  }, []);

  // Get status color and icon (matches legacy)
  const getStatusColor = useCallback((status) => {
    switch (status) {
      case 'ACTIVE':
      case 'CONFIRMED':
        return 'bg-green-100 text-green-800';
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800';
      case 'COMPLETED':
        return 'bg-blue-100 text-blue-800';
      case 'CANCELLED':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }, []);

  // Handle sorting
  const handleSort = useCallback((field) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
  }, [sortField, sortDirection]);

  // Sort function
  const sortReservations = useCallback((reservations) => {
    if (!sortField) return reservations;

    return [...reservations].sort((a, b) => {
      let aValue, bValue;

      switch (sortField) {
        case 'customer':
          aValue = a.customer ? `${a.customer.firstName} ${a.customer.lastName}`.toLowerCase() : '';
          bValue = b.customer ? `${b.customer.firstName} ${b.customer.lastName}`.toLowerCase() : '';
          break;
        case 'service':
          aValue = (a.service?.name || '').toLowerCase();
          bValue = (b.service?.name || '').toLowerCase();
          break;
        case 'startTime':
          aValue = new Date(a.startTime);
          bValue = new Date(b.startTime);
          break;
        case 'hours':
          aValue = parseFloat(a.reservationDuration) || 0;
          bValue = parseFloat(b.reservationDuration) || 0;
          break;
        case 'participants':
          aValue = a.participantCount || 1;
          bValue = b.participantCount || 1;
          break;
        case 'paid':
          aValue = paymentStatus[a.id]?.isPaid ? 1 : 0;
          bValue = paymentStatus[b.id]?.isPaid ? 1 : 0;
          break;
        case 'amount':
          aValue = parseFloat((paymentStatus[a.id]?.amount || '$0.00').replace(/[^0-9.-]/g, '')) || 0;
          bValue = parseFloat((paymentStatus[b.id]?.amount || '$0.00').replace(/[^0-9.-]/g, '')) || 0;
          break;
        case 'source':
          aValue = (paymentStatus[a.id]?.source || '').toLowerCase();
          bValue = (paymentStatus[b.id]?.source || '').toLowerCase();
          break;
        case 'createdBy':
          aValue = a.employee ? `${a.employee.firstName} ${a.employee.lastName}`.toLowerCase() : 'public';
          bValue = b.employee ? `${b.employee.firstName} ${b.employee.lastName}`.toLowerCase() : 'public';
          break;
        default:
          return 0;
      }

      // Handle comparison
      if (aValue < bValue) {
        return sortDirection === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortDirection === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [sortField, sortDirection, paymentStatus]);

  // Fetch reservations (matches legacy API call)
  const fetchReservations = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = {
        page: currentPage,
        limit: 50
      };
      if (filter !== 'all') {
        params.status = filter;
      }
      
      // Add date range parameters for completed reservations
      if (filter === 'COMPLETED' && (dateRange.startDate || dateRange.endDate)) {
        if (dateRange.startDate) {
          params.startDate = dateRange.startDate;
        }
        if (dateRange.endDate) {
          params.endDate = dateRange.endDate;
        }
      }
      
      // Use relative URLs for development environment (includes localhost subdomains)
      const isDevelopment = window.location.hostname.includes('localhost') ||
                           window.location.hostname === '127.0.0.1' ||
                           window.location.port === '3000';
      let apiUrl;

      if (isDevelopment) {
        // In development, use relative URLs so React dev server proxy handles routing
        apiUrl = '/api/reservations/integration/all';
      } else {
        // In production/staging, use the configured API_BASE_URL
        apiUrl = `${API_BASE_URL}/api/reservations/integration/all`;
      }


      // Validate token before making request
      const token = localStorage.getItem('token');

      if (!token || token === 'undefined' || token === 'null' || token.length < 10) {
        console.warn('🔒 CLIENT: Invalid token detected, clearing localStorage and proceeding without authentication');
        localStorage.removeItem('token');
        // Don't throw error - let the server handle authentication
      }

      // Add timestamp to prevent caching
      const timestamp = new Date().getTime();
      const response = await axios.get(apiUrl, {
        params: {
          ...params,
          _t: timestamp  // Cache buster
        },
        headers: {
          ...(token && token !== 'undefined' && token !== 'null' && token.length > 10
            ? { Authorization: `Bearer ${token}` }
            : {}),
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        }
      });
      
      
      let allReservations = response.data.reservations || response.data || [];
      
      // Ensure it's an array
      if (!Array.isArray(allReservations)) {
        console.warn('API returned non-array data:', allReservations);
        allReservations = [];
      }
      
      // Filter for service reservations only (matches legacy filter)
      const serviceReservations = allReservations.filter(reservation => reservation.service);
      
      setReservations(serviceReservations);
      
      // Extract and populate existing payment information
      const initialPaymentStatus = {};
      serviceReservations.forEach(reservation => {
        
        if (reservation.payments && reservation.payments.length > 0) {
          const payment = reservation.payments[0]; // Use the first/latest payment
          
          initialPaymentStatus[reservation.id] = {
            isPaid: true,
            amount: `$${payment.amount.toFixed(2)}`,
            source: payment.paymentType || ''
          };
        } else {
          initialPaymentStatus[reservation.id] = {
            isPaid: false,
            amount: '$0.00',
            source: ''
          };
        }
      });
      
      setPaymentStatus(initialPaymentStatus);
      
      // Set standard payment methods that match Advanced Settings options
      const standardPaymentMethods = new Set(['Venmo', 'Cash App', 'PayPal', 'Zelle', 'Apple Pay']);
      setAvailablePaymentMethods(standardPaymentMethods);
      
    } catch (error) {
      console.error('❌ SERVICES TAB: API call failed!');
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        config: error.config,
        request: error.request ? 'Request object exists' : 'No request object'
      });
      console.error('Full error:', error);

      // Check if it's a JWT malformed error (common in localhost)
      if (error.response?.status === 401 ||
          error.response?.data?.error?.includes('token') ||
          error.message?.includes('token') ||
          error.response?.data?.error?.includes('jwt malformed')) {

        console.warn('🔒 JWT token issue detected. Attempting to refresh authentication...');

        // Check if token exists and is valid format
        const token = localStorage.getItem('token');
        if (!token || token === 'undefined' || token === 'null' || token.length < 10) {
          console.warn('🔒 Invalid token in localStorage:', token);
          setError('Authentication required. Please log in again.');
        } else {
          setError('Authentication issue detected. Please refresh the page or log in again.');
        }
      } else {
        setError('Failed to load service reservations. Please try again.');
      }

      setReservations([]);
    } finally {
      setLoading(false);
    }
  }, [filter, dateRange]);

  // Save payment to server (separate from local state update)
  const savePaymentToServer = useCallback(async (reservationId, currentPaymentState) => {
    try {
      const payload = {
        isPaid: currentPaymentState.isPaid || false,
        amount: currentPaymentState.amount || '$0.00',
        source: currentPaymentState.source || ''
      };
      
      
      // Always use the Railway backend for production domains
      const isProduction = window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1';
      const apiUrl = isProduction
        ? `${API_BASE_URL}/api/payments/reservation/${reservationId}`
        : `/api/payments/reservation/${reservationId}`;
      
      
      const response = await fetch(apiUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        throw new Error(`Failed to update payment: ${response.status}`);
      }
      
      await response.json();
      
      // Don't refresh reservations - local state is already updated correctly
      // and refreshing would overwrite any other local changes the user has made
      
    } catch (error) {
      console.error('💰 CLIENT: Failed to save payment information:', error);
      throw error;
    }
  }, [fetchReservations]);

  // Handle immediate payment changes (local state update only)
  const handlePaymentChange = useCallback((reservationId, field, value) => {
    
    // Update local state immediately for UI responsiveness
    setPaymentStatus(prev => ({
      ...prev,
      [reservationId]: {
        ...prev[reservationId],
        [field]: value
      }
    }));

    // Handle server updates based on field type
    if (field === 'isPaid' || field === 'source') {
      // For checkbox and dropdown, save immediately
      const currentPayment = paymentStatus[reservationId] || {};
      const updatedPayment = { ...currentPayment, [field]: value };
      
      savePaymentToServer(reservationId, updatedPayment).catch(error => {
        // Revert the local state change on error
        setPaymentStatus(prev => ({
          ...prev,
          [reservationId]: {
            ...prev[reservationId],
            [field]: prev[reservationId]?.[field] // Revert to previous value
          }
        }));
        alert(`Failed to save payment: ${error.message || 'Unknown error'}`);
      });
    } else if (field === 'amount') {
      // For amount field, use debounced save
      const timerId = debounceTimers.current[reservationId];
      if (timerId) {
        clearTimeout(timerId);
      }
      
      debounceTimers.current[reservationId] = setTimeout(() => {
        const currentPayment = paymentStatus[reservationId] || {};
        const updatedPayment = { ...currentPayment, [field]: value };
        
        savePaymentToServer(reservationId, updatedPayment).catch(error => {
          console.error('💰 CLIENT: Debounced save failed:', error);
          // Show a subtle error indication but don't revert (user might still be typing)
        });
      }, 1500); // Wait 1.5 seconds after user stops typing
    }
  }, [paymentStatus, savePaymentToServer]);

  // Fetch reservation details (matches legacy)
  const fetchReservationDetails = useCallback(async (reservationId) => {
    try {
      setLoadingDetails(true);
      const response = await axios.get(`/api/reservations/integration/${reservationId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      setDetailedReservation(response.data.data || response.data);
      setShowDetailModal(true);
    } catch (error) {
      console.error('Failed to fetch reservation details:', error);
      alert('Failed to load reservation details');
    } finally {
      setLoadingDetails(false);
    }
  }, []);

  // Handle reservation actions (matches legacy)
  const handleCancelReservation = useCallback(async (reservationId) => {
    if (!window.confirm('Are you sure you want to cancel this reservation?')) {
      return;
    }
    try {
      await axios.post(`/api/reservations/processing/${reservationId}/cancel`, {}, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      fetchReservations();
    } catch (err) {
      console.error('Error cancelling reservation:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to cancel reservation';
      alert(errorMessage);
    }
  }, [fetchReservations]);

  const handleRestoreReservation = useCallback(async (reservationId) => {
    if (!window.confirm('Are you sure you want to restore this cancelled reservation to active status?')) {
      return;
    }
    try {
      await axios.post(`/api/reservations/processing/${reservationId}/restore`, {
        notes: 'Reservation restored to active status'
      }, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      fetchReservations();
    } catch (err) {
      console.error('Error restoring reservation:', err);
      alert('Failed to restore reservation');
    }
  }, [fetchReservations]);

  const handleDeleteReservation = useCallback(async (reservationId) => {
    if (!window.confirm('Are you sure you want to permanently delete this reservation? This action cannot be undone.')) {
      return;
    }
    try {
      await axios.delete(`/api/reservations/processing/${reservationId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      fetchReservations();
    } catch (err) {
      console.error('Error deleting reservation:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to delete reservation';
      alert(errorMessage);
    }
  }, [fetchReservations]);

  const handleCheckIn = useCallback(async (reservationId) => {
    try {
      await axios.post(`/api/reservations/processing/${reservationId}/checkin`, {
        checkedInAt: new Date().toISOString(),
      }, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      fetchReservations();
    } catch (err) {
      console.error('Error checking in customer:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to check in customer';
      alert(errorMessage);
    }
  }, [fetchReservations]);

  const handleUndoCheckIn = useCallback(async (reservationId) => {
    try {
      const confirmed = window.confirm('Are you sure you want to undo this check-in? This will revert the reservation back to confirmed status.');
      if (!confirmed) return;

      await axios.post(`/api/reservations/processing/${reservationId}/undo-checkin`, {}, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      fetchReservations();
    } catch (err) {
      console.error('Error undoing check-in:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to undo check-in';
      alert(errorMessage);
    }
  }, [fetchReservations]);

  // Filter and sort reservations based on search terms (matches legacy logic)
  const filteredAndSortedReservations = sortReservations(reservations.filter(reservation => {
    console.log('🔍 Starting filter for reservation:', {
      id: reservation.id,
      customer: reservation.customer?.firstName + ' ' + reservation.customer?.lastName,
      service: reservation.service?.name,
      serviceId: reservation.service?.id,
      status: reservation.status
    });
    // In check-in mode, show reservations that can be checked in (confirmed, pending payment, or already attended)
    if (isCheckInMode) {
      if (reservation.status !== 'CONFIRMED' &&
          reservation.status !== 'ATTENDED' &&
          reservation.status !== 'PENDING_PAYMENT') {
        return false;
      }
    } else {
      // Apply status filter in normal mode
      if (filter !== 'all' && reservation.status !== filter) {
        return false;
      }
    }

    // Apply search term filter
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      const customerName = reservation.customer ?
        `${reservation.customer.firstName} ${reservation.customer.lastName}`.toLowerCase() : '';
      const serviceName = reservation.service?.name?.toLowerCase() || '';
      const itemNumber = reservation.inventoryItem?.itemNumber?.toLowerCase() || '';

      if (!customerName.includes(searchLower) &&
          !serviceName.includes(searchLower) &&
          !itemNumber.includes(searchLower)) {
        return false;
      }
    }

    // Apply service filter
    if (serviceFilter.trim()) {
      const serviceName = reservation.service?.name?.toLowerCase() || '';
      const serviceId = String(reservation.service?.id || '');
      const filterValue = String(serviceFilter).trim();

      console.log('🔍 Service Filter Debug:', {
        filterValue,
        reservationId: reservation.id,
        serviceName,
        serviceId,
        serviceData: reservation.service
      });

      // Check if filter matches service ID (exact match) or service name (partial match)
      const idMatch = serviceId === filterValue;
      const nameMatch = serviceName.includes(filterValue.toLowerCase());

      // Also check if the filter value is a UUID and we need to match by finding the service
      let serviceNameFromId = '';
      if (filterValue.length > 10 && filterValue.includes('-')) {
        // This looks like a UUID, find the service name from the services array
        const matchingService = services.find(s => String(s.id) === filterValue);
        if (matchingService) {
          serviceNameFromId = matchingService.name.toLowerCase();
        }
      }
      const serviceNameMatch = serviceNameFromId && serviceName === serviceNameFromId;

      console.log('🔍 Filter Match Results:', {
        idMatch,
        nameMatch,
        serviceNameMatch,
        serviceNameFromId
      });

      if (!idMatch && !nameMatch && !serviceNameMatch) {
        return false;
      }
    }

    // Apply customer filter
    if (customerFilter.trim()) {
      const customerName = reservation.customer ?
        `${reservation.customer.firstName} ${reservation.customer.lastName}`.toLowerCase() : '';
      if (!customerName.includes(customerFilter.toLowerCase())) {
        return false;
      }
    }

    console.log('🔍 Reservation passed all filters');
    return true;
  }));

  console.log('🔍 Filtering Summary:', {
    totalReservations: reservations.length,
    filteredCount: filteredAndSortedReservations.length,
    currentFilters: {
      status: filter,
      searchTerm,
      serviceFilter,
      customerFilter
    },
    isCheckInMode
  });

  // Apply pagination
  const totalItems = filteredAndSortedReservations.length;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedReservations = filteredAndSortedReservations.slice(startIndex, endIndex);

  // Initialize component and refetch when filter or date range changes
  useEffect(() => {
    fetchReservations();
  }, [fetchReservations, filter, dateRange.startDate, dateRange.endDate]);

  // Sync status filter with global filters (other filters are used directly from globalFilters)
  useEffect(() => {
    if (globalFilters && globalFilters.status !== undefined) {
      setFilter(globalFilters.status);
    }
  }, [globalFilters]);

  // Cleanup debounce timers on unmount
  useEffect(() => {
    return () => {
      Object.values(debounceTimers.current).forEach(timerId => {
        if (timerId) clearTimeout(timerId);
      });
    };
  }, []);

  // Sortable header component
  const SortableHeader = ({ field, children, className = "col-span-1" }) => (
    <div
      className={`${className} flex items-center cursor-pointer hover:bg-gray-100 py-1 rounded transition-colors`}
      onClick={() => handleSort(field)}
    >
      <span className="select-none">{children}</span>
      {sortField === field && (
        sortDirection === 'asc' ? (
          <ChevronUpIcon className="h-3 w-3 ml-1 text-gray-600" />
        ) : (
          <ChevronDownIcon className="h-3 w-3 ml-1 text-gray-600" />
        )
      )}
    </div>
  );

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <EmptyState
        icon={ExclamationTriangleIcon}
        title="Error Loading Service Reservations"
        description={error}
        action={{
          label: 'Retry',
          onClick: fetchReservations
        }}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header - Mobile Optimized */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-4 sm:space-y-0">
        <div>
          <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">Service Reservations</h2>
          <p className="text-gray-600 mt-1 text-sm sm:text-base">
            Manage service bookings and appointments ({totalItems} total)
          </p>
        </div>

        {/* Check-in Mode Toggle - Mobile Optimized */}
        <div className="flex items-center justify-center sm:justify-end">
          <div className="flex items-center space-x-2 bg-gray-50 rounded-lg p-2">
            <span className={`text-xs sm:text-sm font-medium ${!isCheckInMode ? 'text-gray-900' : 'text-gray-500'}`}>
              Normal
            </span>
            <button
              onClick={() => setIsCheckInMode(!isCheckInMode)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors touch-manipulation ${
                isCheckInMode ? 'bg-blue-600' : 'bg-gray-200'
              }`}
              aria-label="Toggle check-in mode"
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  isCheckInMode ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
            <span className={`text-xs sm:text-sm font-medium ${isCheckInMode ? 'text-gray-900' : 'text-gray-500'}`}>
              Check-In
            </span>
          </div>
        </div>
      </div>



      {/* Date Range Filter for Completed */}
      {filter === 'COMPLETED' && (
        <div className="bg-blue-50 p-4 rounded-lg">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Date
              </label>
              <input
                type="date"
                value={dateRange.startDate}
                onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                End Date
              </label>
              <input
                type="date"
                value={dateRange.endDate}
                onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>
      )}

      {/* Results Header */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium text-gray-900">
          All Service Reservations ({totalItems})
        </h3>
      </div>

      {/* Reservations Table */}
      {paginatedReservations.length === 0 ? (
        <EmptyState
          icon={UserIcon}
          title="No Service Reservations Found"
          description="No service reservations match your current filters. Try adjusting your search criteria."
        />
      ) : isCheckInMode ? (
        <CheckInModeTable
          reservations={paginatedReservations}
          onCheckIn={handleCheckIn}
          onUndoCheckIn={handleUndoCheckIn}
          formatDateTime={formatDateTime}
        />
      ) : (
        <>
          {/* Desktop Table View */}
          <div className="hidden md:block bg-white border border-gray-200 rounded-lg overflow-hidden">
            <table className="min-w-full table-fixed">
              <colgroup>
                <col className="w-48" />
                <col className="w-40" />
                <col className="w-24" />
                <col className="w-32" />
                <col className="w-20" />
                <col className="w-16" />
                <col className="w-24" />
                <col className="w-24" />
                <col className="w-28" />
                <col className="w-20" />
              </colgroup>
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-3 py-3 text-left">
                    <SortableHeader field="customer">
                      <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Customer</span>
                    </SortableHeader>
                  </th>
                  <th className="px-3 py-3 text-left">
                    <SortableHeader field="service">
                      <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Service</span>
                    </SortableHeader>
                  </th>
                  <th className="px-3 py-3 text-left">
                    <SortableHeader field="participants">
                      <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Attendees</span>
                    </SortableHeader>
                  </th>
                  <th className="px-3 py-3 text-left">
                    <SortableHeader field="startTime">
                      <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Start Time</span>
                    </SortableHeader>
                  </th>
                  <th className="px-3 py-3 text-left">
                    <SortableHeader field="hours">
                      <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Hours</span>
                    </SortableHeader>
                  </th>
                  <th className="px-3 py-3 text-left">
                    <SortableHeader field="paid">
                      <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Paid?</span>
                    </SortableHeader>
                  </th>
                  <th className="px-3 py-3 text-left">
                    <SortableHeader field="amount">
                      <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Amount</span>
                    </SortableHeader>
                  </th>
                  <th className="px-3 py-3 text-left">
                    <SortableHeader field="source">
                      <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Source</span>
                    </SortableHeader>
                  </th>
                  <th className="px-3 py-3 text-left">
                    <SortableHeader field="createdBy">
                      <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Created By</span>
                    </SortableHeader>
                  </th>
                  <th className="px-3 py-3 text-left">
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
            {paginatedReservations.map((reservation) => {
              const startDateTime = formatDateTime(reservation.startTime);
              const endDateTime = formatDateTime(reservation.endTime);

              return (
                <tr key={reservation.id} className="hover:bg-gray-50">
                  {/* Customer with Status */}
                  <td className="px-3 py-4 text-sm">
                      <div className="flex items-center space-x-2">
                        {reservation.status === 'ACTIVE' || reservation.status === 'CONFIRMED' ? (
                          <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                            <CheckCircleIcon className="h-4 w-4 text-green-600" />
                          </div>
                        ) : reservation.status === 'COMPLETED' ? (
                          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                            <CheckCircleIcon className="h-4 w-4 text-blue-600" />
                          </div>
                        ) : reservation.status === 'CANCELLED' ? (
                          <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                            <XCircleIcon className="h-4 w-4 text-red-600" />
                          </div>
                        ) : (
                          <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                            <ClockIcon className="h-4 w-4 text-yellow-600" />
                          </div>
                        )}
                        <div>
                          <div className={`text-sm font-medium truncate ${
                            reservation.allergies ? 'text-red-600 cursor-pointer' : 'text-gray-900'
                          }`}
                          title={reservation.allergies ? 'This customer has allergies/dietary restrictions. Click for more details.' : ''}
                          >
                            {reservation.customer ?
                              `${reservation.customer.firstName} ${reservation.customer.lastName}` :
                              'Unknown Customer'
                            }
                            {reservation.allergies && (
                              <span className="ml-1 text-red-500" title="Has allergies/dietary restrictions">⚠️</span>
                            )}
                          </div>
                          <div className={`text-xs px-1 py-0.5 rounded-full inline-block ${getStatusColor(reservation.status)}`}>
                            {reservation.status || 'UNKNOWN'}
                          </div>
                        </div>
                      </div>
                  </td>

                  {/* Service */}
                  <td className="px-3 py-4 text-sm">
                      <div className="text-sm font-medium text-gray-900 truncate" title={reservation.service?.name || 'Unknown Service'}>
                        {reservation.service?.name || 'Unknown Service'}
                      </div>
                      <div className="text-xs text-gray-500">Service</div>
                  </td>

                  {/* Participants */}
                  <td className="px-3 py-4 text-sm">
                      <div className="text-sm text-gray-900">
                        {reservation.participantCount || 1}
                      </div>
                      <div className="text-xs text-gray-500">
                        {(reservation.participantCount || 1) === 1 ? 'person' : 'people'}
                      </div>
                  </td>

                  {/* Start Time */}
                  <td className="px-3 py-4 text-sm">
                      <div className="text-sm text-gray-900">{startDateTime.date}</div>
                      <div className="text-xs text-gray-500">{startDateTime.time}</div>
                  </td>

                  {/* Duration */}
                  <td className="px-3 py-4 text-sm">
                      <div className="text-sm text-gray-900">{reservation.reservationDuration}h</div>
                  </td>

                  {/* Payment Status */}
                  <td className="px-3 py-4 text-sm">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={paymentStatus[reservation.id]?.isPaid || false}
                          onChange={(e) => handlePaymentChange(reservation.id, 'isPaid', e.target.checked)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </label>
                  </td>

                  {/* Amount */}
                  <td className="px-3 py-4 text-sm">
                      <input
                        type="text"
                        value={paymentStatus[reservation.id]?.amount || '$0.00'}
                        onChange={(e) => handlePaymentChange(reservation.id, 'amount', e.target.value)}
                        className="w-full text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        placeholder="$0.00"
                      />
                  </td>

                  {/* Payment Source */}
                  <td className="px-3 py-4 text-sm">
                      <select
                        value={paymentStatus[reservation.id]?.source || ''}
                        onChange={(e) => handlePaymentChange(reservation.id, 'source', e.target.value)}
                        className="w-full text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      >
                        <option value="">—</option>
                        {Array.from(availablePaymentMethods).map(source => (
                          <option key={source} value={source}>{source}</option>
                        ))}
                      </select>
                  </td>

                  {/* Created By */}
                  <td className="px-3 py-4 text-sm">
                      <div className="text-sm text-gray-900">
                        {reservation.employee ?
                          `${reservation.employee.firstName} ${reservation.employee.lastName}` :
                          'Public'
                        }
                      </div>
                  </td>

                  {/* Actions */}
                  <td className="px-3 py-4 text-sm">
                      <div className="flex items-center space-x-2">
                        {hasPermission('reservations', 'view') && (
                          <button
                            onClick={() => fetchReservationDetails(reservation.id)}
                            disabled={loadingDetails}
                            className="text-blue-600 hover:text-blue-900 text-sm font-medium"
                          >
                            <EyeIcon className="h-4 w-4" />
                          </button>
                        )}

                        {reservation.status === 'CANCELLED' && canProcessReturns && (
                          <button
                            onClick={() => handleRestoreReservation(reservation.id)}
                            className="text-green-600 hover:text-green-900 text-sm font-medium"
                            title="Restore reservation"
                          >
                            <ArrowPathIcon className="h-4 w-4" />
                          </button>
                        )}

                        {reservation.status === 'CANCELLED' && hasPermission('reservations', 'delete') && (
                          <button
                            onClick={() => handleDeleteReservation(reservation.id)}
                            className="text-red-600 hover:text-red-900 text-sm font-medium"
                            title="Permanently delete reservation"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        )}

                        {reservation.status !== 'CANCELLED' && reservation.status !== 'COMPLETED' && hasPermission('reservations', 'cancel') && (
                          <button
                            onClick={() => handleCancelReservation(reservation.id)}
                            className="text-red-600 hover:text-red-900 text-sm font-medium"
                            title="Cancel reservation"
                          >
                            <XCircleIcon className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                  </td>
                </tr>
              );
            })}
              </tbody>
            </table>
          </div>

        {/* Mobile Card View */}
        <div className="md:hidden space-y-4">
          {paginatedReservations.map((reservation) => {
            const startDateTime = formatDateTime(reservation.startTime);
            const endDateTime = formatDateTime(reservation.endTime);

            return (
              <div key={reservation.id} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                <div className="space-y-3">
                  {/* Header with Customer and Status */}
                  <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-3">
                      {reservation.status === 'ACTIVE' || reservation.status === 'CONFIRMED' ? (
                        <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <CheckCircleIcon className="h-4 w-4 text-green-600" />
                        </div>
                      ) : reservation.status === 'COMPLETED' ? (
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <CheckCircleIcon className="h-4 w-4 text-blue-600" />
                        </div>
                      ) : reservation.status === 'CANCELLED' ? (
                        <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <XCircleIcon className="h-4 w-4 text-red-600" />
                        </div>
                      ) : (
                        <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <ClockIcon className="h-4 w-4 text-yellow-600" />
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className={`text-sm font-medium ${
                          reservation.allergies ? 'text-red-600' : 'text-gray-900'
                        }`}>
                          {reservation.customer ?
                            `${reservation.customer.firstName} ${reservation.customer.lastName}` :
                            'Unknown Customer'
                          }
                          {reservation.allergies && (
                            <span className="ml-1 text-red-500" title="Has allergies/dietary restrictions">⚠️</span>
                          )}
                        </div>
                        <div className={`text-xs px-2 py-1 rounded-full inline-block mt-1 ${getStatusColor(reservation.status)}`}>
                          {reservation.status || 'UNKNOWN'}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Service Info */}
                  <div className="border-t pt-3">
                    <div className="text-sm font-medium text-gray-900 mb-1">
                      {reservation.service?.name || 'Unknown Service'}
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-xs text-gray-500">
                      <div>
                        <span className="font-medium">Start:</span> {startDateTime.date} at {startDateTime.time}
                      </div>
                      <div>
                        <span className="font-medium">End:</span> {endDateTime.date} at {endDateTime.time}
                      </div>
                    </div>
                    <div className="mt-1 text-xs text-gray-500">
                      <span className="font-medium">Duration:</span> {reservation.reservationDuration}h
                    </div>
                  </div>

                  {/* Payment Section */}
                  <div className="border-t pt-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-900">Payment</span>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={paymentStatus[reservation.id]?.isPaid || false}
                          onChange={(e) => handlePaymentChange(reservation.id, 'isPaid', e.target.checked)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-2"
                        />
                        <span className="text-xs text-gray-600">Paid</span>
                      </label>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Amount</label>
                        <input
                          type="text"
                          value={paymentStatus[reservation.id]?.amount || '$0.00'}
                          onChange={(e) => handlePaymentChange(reservation.id, 'amount', e.target.value)}
                          className="w-full text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500"
                          placeholder="$0.00"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Source</label>
                        <select
                          value={paymentStatus[reservation.id]?.source || ''}
                          onChange={(e) => handlePaymentChange(reservation.id, 'source', e.target.value)}
                          className="w-full text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        >
                          <option value="">—</option>
                          {Array.from(availablePaymentMethods).map(source => (
                            <option key={source} value={source}>{source}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Footer with Creator and Actions */}
                  <div className="flex items-center justify-between pt-3 border-t">
                    <div>
                      <p className="text-xs text-gray-500">Created by</p>
                      <p className="text-sm font-medium">
                        {reservation.employee ?
                          `${reservation.employee.firstName} ${reservation.employee.lastName}` :
                          'Public'
                        }
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      {hasPermission('reservations', 'view') && (
                        <button
                          onClick={() => fetchReservationDetails(reservation.id)}
                          disabled={loadingDetails}
                          className="p-2 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded touch-manipulation"
                          title="View details"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>
                      )}

                      {reservation.status === 'CANCELLED' && canProcessReturns && (
                        <button
                          onClick={() => handleRestoreReservation(reservation.id)}
                          className="p-2 text-green-600 hover:text-green-900 hover:bg-green-50 rounded touch-manipulation"
                          title="Restore reservation"
                        >
                          <ArrowPathIcon className="h-4 w-4" />
                        </button>
                      )}

                      {reservation.status === 'CANCELLED' && hasPermission('reservations', 'delete') && (
                        <button
                          onClick={() => handleDeleteReservation(reservation.id)}
                          className="p-2 text-red-600 hover:text-red-900 hover:bg-red-50 rounded touch-manipulation"
                          title="Permanently delete reservation"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      )}

                      {reservation.status !== 'CANCELLED' && reservation.status !== 'COMPLETED' && hasPermission('reservations', 'cancel') && (
                        <button
                          onClick={() => handleCancelReservation(reservation.id)}
                          className="p-2 text-red-600 hover:text-red-900 hover:bg-red-50 rounded touch-manipulation"
                          title="Cancel reservation"
                        >
                          <XCircleIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        </>
      )}

      {/* Pagination */}
      {totalItems > itemsPerPage && (
        <div className="flex justify-center">
          <Pagination
            currentPage={currentPage}
            totalPages={Math.ceil(totalItems / itemsPerPage)}
            onPageChange={setCurrentPage}
          />
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && detailedReservation && (
        <ReservationDetailsModal
          isOpen={showDetailModal}
          reservation={detailedReservation}
          onClose={() => {
            setShowDetailModal(false);
            setDetailedReservation(null);
          }}
          onReprocess={canProcessReturns ? (reservationId) => {
            // Add reprocess logic here when implemented
          } : null}
          onDelete={hasPermission('reservations', 'delete') ? handleDeleteReservation : null}
          taxRate={orgTaxRate}
          canProcessReturns={canProcessReturns}
          isAdmin={isAdmin}
          onReservationUpdate={(reservationId, updates) => {
            // Update the detailed reservation with new notes
            if (detailedReservation?.id === reservationId) {
              setDetailedReservation({
                ...detailedReservation,
                ...updates
              });
            }

            // Update the reservation in the main list as well
            setReservations(prevReservations =>
              prevReservations.map(r =>
                r.id === reservationId ? { ...r, ...updates } : r
              )
            );
          }}
        />
      )}
    </div>
  );
};

export default memo(ReservationsServicesTab);